/**
 * @fileoverview Floating bulk-action bar.
 *
 * The dark pill that appears at the bottom of the viewport while a selection
 * is active: "N selected", a divider, the surface's actions, then Done. First
 * built inline in the precedent library; extracted here so the timesheet
 * surfaces present the same affordance rather than a second look-alike.
 *
 * Actions are passed as children so each surface supplies its own verbs — use
 * `BulkActionButton` for them so the styling stays consistent.
 */

import type { ReactNode } from "react";

import { Button } from "@/ui/primitives/button";
import { cn } from "@/utils/misc";

/**
 * An action inside the bar. White on the dark pill; `tone="danger"` for
 * destructive verbs (delete / write off).
 */
export function BulkActionButton({
  onClick,
  disabled,
  tone = "default",
  className,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  tone?: "default" | "danger";
  className?: string;
  children: ReactNode;
}) {
  return (
    <Button
      type="button"
      size="sm"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "h-8 gap-1.5 bg-white text-xs hover:bg-white/90",
        tone === "danger" ? "text-red-500 hover:text-red-600" : "text-primary",
        className,
      )}
    >
      {children}
    </Button>
  );
}

export function BulkActionBar({
  count,
  onDone,
  children,
}: {
  /** Number of selected items. The bar hides itself at zero. */
  count: number;
  /** Clears the selection and exits selection mode. */
  onDone: () => void;
  children: ReactNode;
}) {
  if (count === 0) return null;

  return (
    // `pointer-events-none` on the positioner so the bar never blocks clicks
    // on the table beneath it; the pill itself opts back in.
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center px-4">
      <div className="pointer-events-auto flex items-center gap-3 rounded-full border border-primary-foreground/20 bg-primary px-4 py-2 shadow-[0_8px_24px_rgba(0,0,0,0.18),0_2px_6px_rgba(0,0,0,0.12)]">
        <span className="text-sm font-medium text-primary-foreground">
          {count} selected
        </span>
        <div className="h-4 w-px bg-primary-foreground/20" />
        {children}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 text-xs text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground"
          onClick={onDone}
        >
          Done
        </Button>
      </div>
    </div>
  );
}
